import { IStore } from 'redux/IStore';
import {
	IManageUserShippingAddress,
	IManageUserShippingAddressAction,
} from 'models/user/shipping/manageusershippingaddress';
import { IUserShippingAddress } from 'models/user/shipping/usershippingaddress';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
import { browserHistory } from 'react-router';
const { notifSend } = notifActions;

/** Action Types */
export const REQUEST_START: string = 'manageusershippingaddress/REQUEST_START';
export const REQUEST_FAILURE: string = 'manageusershippingaddress/REQUEST_FAILURE';
export const SET_IS_EDIT_MODE: string = 'manageusershippingaddress/SET_IS_EDIT_MODE';
export const CLEAR_FORM_DATA: string = 'manageusershippingaddress/CLEAR_FORM_DATA';
export const GET_USER_SHIPPING_ADDRESS_SUCCESS: string = 'manageusershippingaddress/GET_USER_SHIPPING_ADDRESS_SUCCESS';

/** Initial State */
const initialState: IManageUserShippingAddress = {
	shippingAddress: null,
	isFetching: false,
};

/** Reducer */
export function manageUserShippingAddressReducer(state = initialState, action: IManageUserShippingAddressAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case SET_IS_EDIT_MODE:
			return Object.assign({}, state, {
				isEditMode: action.payload.isEditMode,
			});

		case CLEAR_FORM_DATA:
			return Object.assign({}, state, {
				shippingAddress: null,
			});

		case GET_USER_SHIPPING_ADDRESS_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				shippingAddress: action.payload.shippingAddress,
			});

		default:
			return state;
	}
}

/** Async Action Creator */
export function getUserShippingAddress(id: string) {
	return (dispatch, getState: () => IStore) => {
		if (id) {
			dispatch(setIsEditMode(true));
			dispatch(requestStart());
			return fetch(
				getState().fetchDefault.baseAPI + 'api/User/ShippingAddress/' + id,
				Object.assign({}, getState().fetchDefault.requestInit),
			)
				.then((res) => {
					if (res.ok) {
						return res.json().then((res: IUserShippingAddress) => {
							dispatch(getUserShippingAddressSuccess(res));
						});
					} else {
						const status: number = res.status;
						const statusText: string = res.statusText;
						return res
							.json()
							.then((res) => {
								res.status = status;
								res.statusText = statusText;
								dispatch(requestFailure(res));
							})
							.catch(() => {
								const message: any = { status, statusText };
								dispatch(requestFailure(message));
							});
					}
				})
				.catch((err) => {
					dispatch(requestFailure(err));
				});
		} else {
			dispatch(setIsEditMode(false));
			dispatch(clearFormData());
		}
	};
}

/** Action Creator - create */
export function submitCreateUserShippingAddress(shippingAddress: IUserShippingAddress) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		const userId: string = getState().userPreferences.id;
		shippingAddress.userId = userId;

		return fetch(
			getState().fetchDefault.baseAPI + 'api/User/ShippingAddress',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(shippingAddress),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((newAddress: IUserShippingAddress) => {
						dispatch(getUserShippingAddressSuccess(newAddress));
						dispatch(
							notifSend({
								message: i18n.t('Notification_Success_User_Shipping_ManageShippingAddress_Created'),
								kind: 'success',
								dismissAfter: 3000,
							}),
						);
						browserHistory.push('/user/edit/shippingaddresses');
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(getUserShippingAddressSuccess(shippingAddress));
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator - create */
export function submitUpdateUserShippingAddress(shippingAddress: IUserShippingAddress) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/User/ShippingAddress/' + shippingAddress.id,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
				body: JSON.stringify(shippingAddress),
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(getUserShippingAddressSuccess(shippingAddress));
					dispatch(
						notifSend({
							message: i18n.t('Notification_Success_User_Shipping_ManageShippingAddress_Updated'),
							kind: 'success',
							dismissAfter: 3000,
						}),
					);
					browserHistory.push('/user/edit/shippingaddresses');
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(getUserShippingAddressSuccess(shippingAddress));
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function requestStart(): IManageUserShippingAddressAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IManageUserShippingAddressAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function setIsEditMode(isEditMode: boolean): IManageUserShippingAddressAction {
	return {
		type: SET_IS_EDIT_MODE,
		payload: {
			isEditMode,
		},
	};
}

/** Action Creator */
export function clearFormData(): IManageUserShippingAddressAction {
	return {
		type: CLEAR_FORM_DATA,
	};
}

/** Action Creator */
export function getUserShippingAddressSuccess(shippingAddress: IUserShippingAddress): IManageUserShippingAddressAction {
	return {
		type: GET_USER_SHIPPING_ADDRESS_SUCCESS,
		payload: {
			shippingAddress,
		},
	};
}
